import { useState } from "react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { ArrowLeft, Save } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { useHistory } from "react-router-dom";
import { addProfile, AuthProvider } from "../../pt/utils/http-service";
import { useAuth } from "../../context/AuthContext";
const CreateClient = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    birthDate: new Date(),
    height: 0,
    weight: "",
    gender: "male" as "male" | "female",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const history = useHistory();
  const { toast } = useToast();
  const auth = new AuthProvider(useAuth().getAccessTokenSilently);

  const handleInputChange = (field: string, value: string | Date) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.birthDate ||
      !formData.height
    ) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }
    setIsSubmitting(true);
    try {
        const ageYears = new Date().getFullYear() - formData.birthDate.getFullYear();
        if (ageYears < 18 || ageYears > 120) {
            toast({
                title: "Validation Error",
                description: "Age must be between 18 and 120 years based on birth date.",
                variant: "destructive",
            });
            setIsSubmitting(false);
            return;
        }

      const res = await addProfile(
        {
          ...formData,
          ...(formData.weight !== "" ? { weight: Number(formData.weight) } : {}),
          account: "",
          ID: "",
          createdAt: new Date(),
          age: ageYears,
        },
        auth
      );
      console.log("Client created successfully:", res);

      toast({
        title: "Client Created",
        description: "New client has been successfully created.",
        variant: "default",
      });
      history.push("/clients");
      return;
    } catch (error) {
      console.error("Error creating client:", error);
      toast({
        title: "Error",
        description:
          "There was an error creating the client. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8 ">
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in ">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => history.push("/clients")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Clients
          </Button>
        </div>

        <Card className="p-2 overflow-auto max-h-[calc(100vh-4rem)]">
          <div className="space-y-2 overflow-y-scroll overflow-scroll">
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold text-gray-900">
                Create New Client
              </h1>

            </div>

            <form onSubmit={handleSubmit} className="space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Enter first name"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Enter last name"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="birthDate">Birth Date *</Label>
                  <Input
                    id="birthDate"
                    type="date"
                    placeholder="Enter birth date"
                    value={formData.birthDate.toISOString().split("T")[0]}
                    onChange={(e) => handleInputChange("birthDate", new Date(e.target.value))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="height">Height (cm) *</Label>
                  <Input
                    id="height"
                    type="number"
                    placeholder="Enter height in cm"
                    value={formData.height}
                    onChange={(e) =>
                      handleInputChange("height", e.target.value)
                    }
                    min="100"
                    max="250"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weight">Weight (kg)</Label>
                  <Input
                    id="weight"
                    type="number"
                    placeholder="Enter weight in kg"
                    value={formData.weight}
                    onChange={(e) =>
                      handleInputChange("weight", e.target.value)
                    }
                    min="1"
                    max="500"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label>Gender *</Label>
                <RadioGroup
                  value={formData.gender}
                  onValueChange={(value: "male" | "female") =>
                    handleInputChange("gender", value)
                  }
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="male" id="male" />
                    <Label htmlFor="male">Male</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="female" id="female" />
                    <Label htmlFor="female">Female</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex space-x-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => history.push("/clients")}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Create Client
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CreateClient;
